import { Feature, LineItem, OrderProductPreview, PlanInterval, Subscription } from '../models';
import { USD_CURRENCY } from './Constants';
import { TrackingPayload } from './utils';

type AnalyticsCartItemSource = 'order_preview' | 'subscription_view';
type AnalyticsItemType = 'trial_product' | 'product' | 'subscription';

export interface AnalyticsItem {
  item_id?: string;
  item_name?: string;
  price?: number;
  quantity: number;
  discount?: number;
  billing_interval?: string;
  subtotal?: number;
  total?: number;
  credit?: number;
  tax?: number;
  proration_rate?: number;
  item_type: AnalyticsItemType;
  source: AnalyticsCartItemSource;
  addOn?: LineItem[]
}

export interface AnalyticsCartSnapshot {
  currency: string;
  value: number;
  items: AnalyticsItem[];
}

interface BuildPlanCartSnapshotOptions {
  currency?: string;
  productsPreview?: OrderProductPreview[];
}

const toNumber = (value?: number | null): number => (typeof value === 'number' ? value : 0);

const resolveBillingInterval = (interval?: PlanInterval | null): string | undefined => (
  interval ? PlanInterval.getPlanIntervalCode(interval) : undefined
);

const resolveFeature = (feature?: Feature | null): string | undefined => (
  feature ? Feature.toString(feature) : undefined
);

const mapOrderProductToAnalyticsItem = (preview: OrderProductPreview): AnalyticsItem => {
  const subtotal = toNumber(preview.subTotal);
  const discountAmount = toNumber(preview.discountAmount);
  const creditAmount = toNumber(preview.creditAmount);
  const taxAmount = toNumber(preview.tax);
  const price = subtotal - discountAmount - creditAmount + taxAmount;

  return {
    item_id: preview.productCode,
    item_name: resolveFeature(preview.feature),
    billing_interval: resolveBillingInterval(preview.billingInterval),
    quantity: toNumber(preview.newQuantity),
    price: toNumber(preview.unitAmount),
    subtotal,
    total: price,
    discount: discountAmount,
    credit: creditAmount,
    tax: taxAmount,
    proration_rate: toNumber(preview.prorationRate),
    item_type: 'product',
    source: 'order_preview',
    addOn: preview.lineItem,
  };
};

const mapSubscriptionToAnalyticsItem = (
  subscription: Subscription,
): AnalyticsItem => ({
  item_id: subscription.productCode,
  item_name: resolveFeature(subscription.feature),
  billing_interval: resolveBillingInterval(subscription.billingInterval),
  quantity: toNumber(subscription.quota),
  item_type: 'subscription',
  source: 'subscription_view',
});

export const buildPlanCartSnapshot = ({
  currency = USD_CURRENCY,
  productsPreview = [],
}: BuildPlanCartSnapshotOptions): AnalyticsCartSnapshot => {
  const items = productsPreview.map(mapOrderProductToAnalyticsItem);

  if (items.length === 0) {
    return {
      currency,
      value: 0,
      items: [],
    };
  }

  const value = items.reduce((accumulator, item) => accumulator + (item.total ?? 0), 0);

  return {
    currency,
    value,
    items,
  };
};

export const withCartSnapshot = (payload: TrackingPayload, cartSnapshot: AnalyticsCartSnapshot) => (
  cartSnapshot
    ? {
      ...payload,
      value: cartSnapshot.value,
      currency: cartSnapshot.currency,
      items: cartSnapshot.items,
    }
    : payload
);

export const withSubscriptions = (
  payload: TrackingPayload,
  subscriptions: Subscription[],
) => (
  {
    ...payload,
    value: null, // temporarily set to null now, will be updated later
    currency: USD_CURRENCY,
    items: subscriptions.length > 0 ? subscriptions.map(mapSubscriptionToAnalyticsItem) : [],
  }
);
